/**
 * Service helper estratti da `levelUp.ts` per costruzione del
 * `LevelUpRecord` (audit log immutabile della transizione).
 *
 * Estrazione effettuata in D7 (FI-17) per rispettare il limite ESLint
 * `max-lines: 400` su `levelUp.ts` (gli helper di audit sommavano ~85
 * righe in più ai service originali D1-D6).
 *
 * Le funzioni qui sono puramente builders (no side-effect):
 *  - `buildLevelUpRecord(input, moveDef, currentMoves, nextMoves, levelReached)` →
 *    costruisce il `LevelUpRecord` finale popolando i campi opzionali
 *    quando applicabili (`statIncreased`, `secondaryArchetypeAdded`,
 *    `grantedChoice`, `multiclassPick`). **D7-bundle (FI-17 follow-up)**:
 *    `movesBundled` è derivato dal diff `nextMoves \ currentMoves`
 *    escludendo gli ID noti (`moveAdded`, granted, multiclassPick).
 *  - `buildGrantedChoiceAudit(grant, selectedOptionId, currentMoves)` →
 *    deriva il discriminated `LevelUpRecord.grantedChoice` per i 3 kind
 *    (`from-group`, `from-choice-move`, `from-choice-mapping`).
 */

import type { MoveRef } from '../schemas/character';
import type { LevelUpRecord } from '../schemas/levelUpRecord';
import type { Move } from '../schemas/move';
import type { LevelUpInput } from './levelUp';

export function buildLevelUpRecord(
  input: LevelUpInput,
  moveDef: Move,
  currentMoves: readonly MoveRef[],
  nextMoves: readonly MoveRef[],
  levelReached: number,
): LevelUpRecord {
  const ownedIds = new Set(currentMoves.map((m) => m.id));
  const movesReplaced = moveDef.replaces.filter((id) => ownedIds.has(id));
  const spellsAdded = input.spellsAdded ? input.spellsAdded.map((s) => ({ id: s.id })) : [];
  // **D7-bundle (FI-17 follow-up)**: `movesBundled` = mosse aggiunte
  // durante il level-up che NON sono main / granted (from-group/mapping) /
  // multiclassPick. Calcolato come diff `nextMoves \ currentMoves` filtrato
  // sui kind noti non-bundle.
  const knownNonBundleIds = new Set<string>([input.moveAdded.id]);
  if (
    input.grantedChoice !== undefined &&
    moveDef.grantsMoveChoice !== undefined &&
    (moveDef.grantsMoveChoice.kind === 'from-group' ||
      moveDef.grantsMoveChoice.kind === 'from-choice-mapping')
  ) {
    knownNonBundleIds.add(input.grantedChoice.selectedOptionId);
  }
  if (input.multiclassPick !== undefined) {
    knownNonBundleIds.add(input.multiclassPick.selectedMoveId);
  }
  const movesBundled = nextMoves
    .map((m) => m.id)
    .filter((id) => !ownedIds.has(id) && !knownNonBundleIds.has(id));
  const record: LevelUpRecord = {
    levelReached,
    timestamp: new Date().toISOString(),
    moveAdded: input.moveAdded.id,
    movesReplaced,
    movesBundled,
    spellsAdded,
  };
  if (input.statIncreased !== undefined) record.statIncreased = input.statIncreased;
  if (input.secondaryArchetypeId !== undefined) {
    record.secondaryArchetypeAdded = input.secondaryArchetypeId;
  }
  // FI-46 PR-B+: propaga la mappa pick (grantKey → spellIds) per abilitare
  // pre-popolazione dello step spell in edit retroattivo. Salva solo se
  // ci sono effettivamente pick (skip mappe vuote per non sporcare l'audit).
  if (input.spellSelections !== undefined) {
    const nonEmptyEntries = Object.entries(input.spellSelections).filter(
      ([, ids]) => ids.length > 0,
    );
    if (nonEmptyEntries.length > 0) {
      record.spellGrantSelections = Object.fromEntries(
        nonEmptyEntries.map(([k, ids]) => [k, [...ids]]),
      );
    }
  }
  // FI-31 (D5) + FI-30 (D6): audit della sotto-scelta concessa, se presente.
  if (input.grantedChoice !== undefined && moveDef.grantsMoveChoice !== undefined) {
    record.grantedChoice = buildGrantedChoiceAudit(
      moveDef.grantsMoveChoice,
      input.grantedChoice.selectedOptionId,
      currentMoves,
    );
  }
  // FI-17 (D7): audit del pick multiclasse, se presente.
  if (input.multiclassPick !== undefined && moveDef.multiclass !== undefined) {
    record.multiclassPick = {
      sourceClassId: input.multiclassPick.sourceClassId,
      selectedMoveId: input.multiclassPick.selectedMoveId,
    };
  }
  return record;
}

/**
 * **FI-30 (D6)**: per `from-choice-mapping`, l'audit richiede di
 * identificare la `sourceChoiceId` che ha guidato la mappatura. La
 * ricaviamo trovando l'inversa: cerca nel mapping qual è la choice
 * che punta alla mossa selezionata. In edge case di mapping
 * non-iniettivo (più choice → stessa mossa), prende la prima.
 */
export function buildGrantedChoiceAudit(
  grant: NonNullable<Move['grantsMoveChoice']>,
  selectedOptionId: string,
  currentMoves: readonly MoveRef[],
): LevelUpRecord['grantedChoice'] {
  if (grant.kind === 'from-group') {
    return {
      kind: 'from-group',
      groupId: grant.groupId,
      selectedOptionId,
    };
  }
  if (grant.kind === 'from-choice-move') {
    return {
      kind: 'from-choice-move',
      sourceMoveId: grant.moveId,
      selectedOptionId,
    };
  }
  if (grant.kind === 'from-list') {
    // FI-82: audit minimale, pool inline non richiede risalita.
    return { kind: 'from-list', selectedOptionId };
  }
  // from-choice-mapping: ricava sourceChoiceId dalla mappa o dal MoveRef sorgente.
  const sourceRef = currentMoves.find((m) => m.id === grant.sourceMoveId);
  const sourceSelections = new Set<string>([
    ...(sourceRef?.selectedChoices ?? []),
    ...(sourceRef?.selectedChoiceGroups?.main ?? []),
  ]);
  // Preferiamo una choice effettivamente selezionata dal PG.
  const sourceChoiceId = Object.entries(grant.mapping).find(
    ([choiceId, moveId]) => moveId === selectedOptionId && sourceSelections.has(choiceId),
  )?.[0];
  // Fallback (caso patologico, mapping non-iniettivo o sorgente assente):
  // ricava qualunque chiave che punti alla mossa selezionata.
  const fallbackChoiceId =
    sourceChoiceId ??
    Object.entries(grant.mapping).find(([, moveId]) => moveId === selectedOptionId)?.[0] ??
    '';
  return {
    kind: 'from-choice-mapping',
    sourceMoveId: grant.sourceMoveId,
    sourceChoiceId: fallbackChoiceId,
    selectedOptionId,
  };
}
